import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FormGroup from '@material-ui/core/FormGroup';
import styles from './styles.module.scss';
import { setTimeout } from 'timers';
import {
  ModuleWith,
  PowerSwitchState,
  PowerUnit,
  PowerSwitch,
  PowerUnitState,
  SwitchingState,
} from '@/slicers/operation_page';
import { CustomButton } from '@/components/atoms/CustomButton';
import { switchMaxAmount } from '@/config';
import { useHistory, useLocation } from 'react-router';
import SwitchSimulator64CH from '@/components/organisms/SwitchSimulator64CH';

const createSwitch = (num: number, state = PowerSwitchState.UNKNOWN) => {
  return {
    id: num + 1,
    number: num,
    state,
    name: (num + 1).toString(),
    shutterMode: 0,
    isLock: 0,
    switchingState: SwitchingState.STANDBY,
    icon: '',
  } as PowerSwitch;
};

const createUnit = (num: number) => {
  return {
    number: num,
    timestamp: 0,
    state: PowerUnitState.READY,
    switchesByNumber: {},
  } as PowerUnit;
};

export const createCommModule = (deviceCode: string): ModuleWith => {
  return {
    id: 1,
    deviceCode,
    name: '',
    switchingState: SwitchingState.STANDBY,
    unitsByNumber: {} as Record<string, PowerUnit>,
  };
};

export default function WebSocketClientModule64CH() {
  const location = useLocation();
  const history = useHistory();
  const [ws, setws] = useState<WebSocket>();
  const [groupIdx, setGroupIndex] = useState(0);
  const [signalRAskingID, setSignalRAskingID] = useState(0);
  const [wsClosed, setWsClosed] = useState(true);

  const autoConnect = useMemo(() => {
    const check = new URLSearchParams(location.search).get('auto_connect');
    if (check == null || check === '0' || check === 'false') return false;

    return true;
  }, [location.search]);

  const [deviceCode, setDeviceCode] = useState(
    () => new URLSearchParams(location.search).get('device_code') || 'SPT123'
  );

  const [fwvVersion, setFwvVersion] = useState(
    () => new URLSearchParams(location.search).get('fwv') || '0.1.0'
  );

  const [moduleWith, setModuleWith] = useState<ModuleWith>(() =>
    createCommModule(deviceCode)
  );

  const observeValues = useRef<{
    simulatePending: boolean;
    units: PowerUnit[];
    lastSubmitDeviceCode?: string;
    lastPressUnit?: number;
    unitsByGroup: Record<string, PowerUnit[]>;
    handleMessage: (message: MessageEvent) => void;
    fwvVersion: string;
  }>({} as any);

  const units = useMemo(() => {
    return Object.values(moduleWith.unitsByNumber).sort(
      (u1, u2) => u1.number - u2.number
    );
  }, [moduleWith.unitsByNumber]);

  const unitsByGroup = useMemo(() => {
    return units.reduce((obj, unit) => {
      const groupIdx = Math.ceil(unit.number / 4) - 1;
      obj[groupIdx] = obj[groupIdx] || [];
      obj[groupIdx].push(unit);
      return obj;
    }, {});
  }, [units]);

  const groupAmount = Object.keys(unitsByGroup).length;

  const handleClickConnectBtn = useCallback(
    (deviceCode: string) => {
      if (!ws) return;
      observeValues.current.lastSubmitDeviceCode = deviceCode;
      history.replace(`?device_code=${deviceCode}`);
      setModuleWith(createCommModule(deviceCode));
      ws.send(`SPID=${[deviceCode]}`);
      console.log('SPID= sended');
    },
    [ws, history]
  );

  const createREqualRequest = useCallback(
    (unitNumber: number) => {
      const powerUnit = moduleWith.unitsByNumber[unitNumber];

      let bits = 0;
      for (
        let switchNumber = 0;
        switchNumber < switchMaxAmount;
        switchNumber++
      ) {
        const state = powerUnit?.switchesByNumber[switchNumber]?.state;
        const bit =
          state === PowerSwitchState.LEAK
            ? 3
            : state === PowerSwitchState.ON
              ? 2
              : state === PowerSwitchState.OFF
                ? 1
                : 0;
        bits = bits | (bit << (2 * (switchMaxAmount - switchNumber - 1)));
      }

      return `R=${unitNumber},${bits}`;
    },
    [moduleWith.unitsByNumber]
  );

  const handleReceivedCADR = (pattern: string) => {
    const groups: string[] = pattern.split(',');

    setModuleWith((prev) => {
      const newMdl = JSON.parse(JSON.stringify(prev)) as ModuleWith;
      groups.forEach((group, groupIdx) => {
        const isLostGroup = group === '*';
        if (isLostGroup) {
          // Remove lost group units
          const units = observeValues.current.unitsByGroup[groupIdx] || [];
          for (const unit of units) {
            delete newMdl.unitsByNumber[unit.number];
          }
        } else {
          const binary = (+group).toString(2); // convert decimal pattern to binary switches ( 32768 => 1000000000000000 )
          binary.split('').forEach((c, idx) => {
            const switchIdx = idx % 4;
            const unitNumber = groupIdx * 4 + Math.floor(idx / 4) + 1;
            const isSwitchGone = c === '0';

            if (isSwitchGone) {
              // Remove lost switch
              if (newMdl.unitsByNumber[unitNumber]) {
                delete newMdl.unitsByNumber[unitNumber].switchesByNumber[idx];
              }
            } else {
              newMdl.unitsByNumber[unitNumber] =
                newMdl.unitsByNumber[unitNumber] || createUnit(unitNumber);

              // Create switch
              if (
                !newMdl.unitsByNumber[unitNumber].switchesByNumber[switchIdx]
              ) {
                newMdl.unitsByNumber[unitNumber].switchesByNumber[switchIdx] =
                  createSwitch(switchIdx, PowerSwitchState.OFF);
              }
            }
          });
        }
      });

      return newMdl;
    });
  };

  observeValues.current.units = units;
  observeValues.current.unitsByGroup = unitsByGroup;
  observeValues.current.fwvVersion = fwvVersion;
  observeValues.current.handleMessage = (message: MessageEvent) => {
    if (observeValues.current.simulatePending) return;

    console.log('onmessage', message);
    if (message.data.substr(0, 5) === 'CADR=') {
      const [, pattern] = message.data.split('='); // CADR=XXXX,*,XXXX,*
      handleReceivedCADR(pattern);
    } else if (message.data.substr(0, 2) === 'R?') {
      setSignalRAskingID(Date.now());
    } else if (message.data.substr(0, 3) === 'SW=') {
      setModuleWith((moduleWith) => {
        // スイッチ変更要求
        const payload = message.data.substr(3);
        const p = payload.split(',');
        const unitNumber = Number(p[0]);
        const bits = Number(p[1]);

        const _moduleWith = JSON.parse(
          JSON.stringify(moduleWith)
        ) as ModuleWith;
        const powerUnit = _moduleWith.unitsByNumber[unitNumber];

        // 下位bitから1bitずつスイッチ1,スイッチ2...スイッチ8となっている
        // bitが1のスイッチはONとOFFを反転させる
        for (
          let switchNumber = switchMaxAmount - 1;
          switchNumber >= 0;
          switchNumber--
        ) {
          if ((bits >> (switchMaxAmount - 1 - switchNumber)) & 1) {
            powerUnit.switchesByNumber[switchNumber].state =
              powerUnit.switchesByNumber[switchNumber].state ===
              PowerSwitchState.ON
                ? PowerSwitchState.OFF
                : PowerSwitchState.ON;
          }
        }

        _moduleWith.unitsByNumber[unitNumber] = powerUnit;

        return _moduleWith;
      });
    } else if (message.data.substr(0, 3) === 'RST') {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('auto_connect', 'true');
      currentUrl.searchParams.set(
        'device_code',
        observeValues.current.lastSubmitDeviceCode
      );
      currentUrl.searchParams.set('fwv', observeValues.current.fwvVersion);
      window.location.href = currentUrl.toString();
    } else if (message.data.trim() === 'FWV?') {
      const fwvResponse = `FWV=${observeValues.current.fwvVersion}`;
      const wsTarget = message.target as WebSocket;
      if (wsTarget && wsTarget.readyState === WebSocket.OPEN) {
        wsTarget.send(fwvResponse);
        console.log('send', fwvResponse);
      }
    }
  };

  useEffect(() => {
    if (!wsClosed) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(
      `${protocol}://${
        process.env.REACT_APP_WEB_SOCKET_URL || 'localhost:3004'
      }/ws/`
    );

    ws.onopen = () => {
      console.log('onopen');
      setws(ws);
      setWsClosed(false);
    };

    ws.onclose = async () => {
      console.log('onclose');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setWsClosed(true);
      window.location.reload();
    };

    ws.onmessage = observeValues.current.handleMessage;
  }, [wsClosed]);

  useEffect(() => {
    if (!signalRAskingID) return;
    if (!ws) return;
    if (observeValues.current.simulatePending) return;

    const lastPressUnit = observeValues.current!.lastPressUnit;

    const unitsToSend =
      lastPressUnit != null
        ? units.filter((u) => u.number === lastPressUnit)
        : units;

    for (const unit of unitsToSend) {
      const rEqualRequest = createREqualRequest(unit.number);
      ws.send(rEqualRequest);
      console.log('send', rEqualRequest);
    }
    observeValues.current!.lastPressUnit = undefined; // Always reset last press unit after send R
  }, [ws, units, signalRAskingID, createREqualRequest]);

  const handleClickSwitchButton = ({ state, switchNumber, unitNumber }) => {
    let _moduleWith = JSON.parse(JSON.stringify(moduleWith)) as ModuleWith;
    _moduleWith.unitsByNumber[unitNumber].switchesByNumber[switchNumber].state =
      state;
    observeValues.current!.lastPressUnit = unitNumber;
    setModuleWith(_moduleWith);
  };

  const handleChangeGroup = (idx: number) => () => {
    const nextIndex = groupIdx + idx;
    setGroupIndex(nextIndex);
  };

  // Handle auto connect
  useEffect(() => {
    if (!autoConnect) return;
    handleClickConnectBtn(deviceCode);
  }, [autoConnect, handleClickConnectBtn, deviceCode]);

  return (
    <FormGroup>
      <div className={styles.container}>
        <div className={styles.unitGroupChanger}>
          <CustomButton
            disabled={groupIdx < 1}
            color="info"
            onClick={handleChangeGroup(-1)}
          >
            {'<'}
          </CustomButton>
          <span>
            {groupIdx + 1}/{groupAmount}
          </span>
          <CustomButton
            disabled={groupIdx > groupAmount - 2}
            color="info"
            onClick={handleChangeGroup(1)}
          >
            {'>'}
          </CustomButton>
        </div>
        <div>
          デバイスコード（SPID）
          <input
            defaultValue={deviceCode}
            onBlur={(e) => {
              setDeviceCode(e.target.value);
            }}
          />
          <button
            disabled={!ws}
            onClick={() => handleClickConnectBtn(deviceCode)}
          >
            接続
          </button>
        </div>
        <div>
          FWVバージョン
          <input
            style={{ marginLeft: 14 }}
            defaultValue={fwvVersion}
            onBlur={(e) => {
              setFwvVersion(e.target.value);
            }}
          />
        </div>
        {!!signalRAskingID && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center' }}>
            <input
              style={{ width: 20, height: 20 }}
              type="checkbox"
              onChange={(e) => {
                observeValues.current.simulatePending = e.target.checked;
                if (!e.target.checked) {
                  setSignalRAskingID(Date.now());
                }
              }}
            />
            <span>Simulate pending</span>
          </div>
        )}
        <div>
          <SwitchSimulator64CH
            groupIdx={groupIdx}
            units={unitsByGroup[groupIdx] || []}
            handleClickSwitchButton={handleClickSwitchButton}
          />
        </div>
      </div>
    </FormGroup>
  );
}
